@if (enabled()) {
  @if (isOpen() && isMobile()) {
    <div class="agent-backdrop" (click)="close()"></div>
  }

  <aside
    #panelEl
    class="agent-panel"
    [class.agent-panel--open]="isOpen()"
    [class.agent-panel--mobile]="isMobile()"
    role="complementary"
    [attr.aria-label]="'agent.title' | translate"
    [inert]="!isOpen()">
    <div
      class="agent-panel__resize-handle"
      role="separator"
      aria-orientation="vertical"
      tabindex="0"
      [attr.aria-label]="'agent.resize' | translate"
      (pointerdown)="onResizeStart($event)"
      (pointermove)="onResizeMove($event)"
      (pointerup)="onResizeEnd()"
      (pointercancel)="onResizeEnd()"
      (keydown)="onResizeKeydown($event)"></div>
    <header class="agent-panel__header">
      <span class="agent-panel__header-icon" aria-hidden="true"></span>
      <span class="agent-panel__title">{{ "agent.title" | translate }}</span>
      <button
        type="button"
        class="agent-panel__history-toggle"
        mat-icon-button
        [class.agent-panel__history-toggle--active]="showHistory()"
        [attr.aria-label]="'agent.history.title' | translate"
        [attr.aria-pressed]="showHistory()"
        [matTooltip]="'agent.history.title' | translate"
        (click)="toggleHistory()">
        <mat-icon>history</mat-icon>
      </button>
      @if (hasMessages()) {
        <button
          type="button"
          class="agent-panel__new"
          mat-icon-button
          [attr.aria-label]="'agent.new-conversation' | translate"
          [matTooltip]="'agent.new-conversation' | translate"
          (click)="newConversation()">
          <mat-icon>add_comment</mat-icon>
        </button>
      }
      <button type="button" class="agent-panel__close" mat-icon-button [attr.aria-label]="'agent.close' | translate" (click)="close()">
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <div class="agent-panel__live-region" aria-live="polite">{{ liveAnnouncement() }}</div>

    @if (showHistory()) {
      <div class="agent-panel__messages">
        <app-agent-history></app-agent-history>
      </div>
    } @else {
      <div #scrollContainer class="agent-panel__messages" (scroll)="onMessagesScroll()">
        @if (hasMessages()) {
          <app-agent-message-list
            [messages]="messages()"
            (approve)="onApprove($event)"
            (deny)="onDeny($event)"
            (pickTemplate)="onPickTemplate($event)"
            (openAudience)="onOpenAudience($event)"
            (rate)="onRate($event)"
            (retry)="onRetry()"></app-agent-message-list>
        } @else {
          <div class="agent-panel__empty">
            <mat-icon class="agent-panel__empty-icon">auto_awesome</mat-icon>
            <p class="agent-panel__empty-intro">{{ emptyIntroKey() | translate }}</p>
            @if (examplePrompts().length > 0) {
              <div class="agent-panel__examples">
                @for (example of examplePrompts(); track example) {
                  <button type="button" class="agent-panel__example" (click)="sendExample(example)">
                    {{ example }}
                  </button>
                }
              </div>
            }
          </div>
        }
      </div>
    }

    @if (!showHistory() && suggestions().length > 0) {
      <div class="agent-panel__suggestions">
        @for (suggestion of suggestions(); track suggestion) {
          <button type="button" class="agent-panel__suggestion" (click)="sendExample(suggestion)">{{ suggestion }}</button>
        }
      </div>
    }

    <!-- Hidden rather than removed while history is open, so the draft survives the round trip. -->
    <form class="agent-panel__composer" [class.agent-panel__composer--hidden]="showHistory()" (ngSubmit)="send()">
      @if (mentionOpen()) {
        <ul id="agent-mention-list" class="agent-panel__mentions" role="listbox" [attr.aria-label]="'agent.mention-suggestions' | translate">
          @for (option of mentionResults(); track option.kind + ":" + option.id; let i = $index) {
            <li
              [id]="'agent-mention-' + i"
              class="agent-panel__mention"
              [class.agent-panel__mention--active]="i === mentionActiveIndex()"
              role="option"
              [attr.aria-selected]="i === mentionActiveIndex()"
              (mousedown)="$event.preventDefault()"
              (click)="selectMention(i)">
              <span class="agent-panel__mention-label">{{ option.label }}</span>
              <span class="agent-panel__mention-hint">{{ option.hint }}</span>
            </li>
          }
        </ul>
      }
      @if (showJumpToLatest()) {
        <button
          type="button"
          class="agent-panel__jump-latest"
          mat-mini-fab
          [attr.aria-label]="'agent.jump-to-latest' | translate"
          [matTooltip]="'agent.jump-to-latest' | translate"
          matTooltipPosition="above"
          (click)="jumpToLatest()">
          <mat-icon>arrow_downward</mat-icon>
        </button>
      }
      <!-- Border/background on the wrapper so the editable region stays borderless and can grow/scroll. -->
      <div class="agent-panel__input-wrap">
        <!-- contenteditable rather than a textarea so @-mentions render as atomic chips inline; the
             editor serializes back to { text, mentions } on send (see agent-composer-dom). -->
        <div
          #composerInput
          class="agent-panel__input"
          [class.agent-panel__input--empty]="isEmpty()"
          contenteditable="true"
          role="textbox"
          aria-multiline="true"
          aria-autocomplete="list"
          [attr.aria-controls]="mentionOpen() ? 'agent-mention-list' : null"
          [attr.aria-activedescendant]="mentionOpen() ? 'agent-mention-' + mentionActiveIndex() : null"
          [attr.aria-label]="'agent.input-placeholder' | translate"
          [attr.data-placeholder]="'agent.input-placeholder' | translate"
          (keydown)="onKeydown($event)"
          (input)="onComposerInput()"
          (click)="onComposerCaretMove()"
          (keyup)="onComposerKeyup($event)"
          (paste)="onPaste($event)"
          (beforeinput)="onBeforeInput($event)"
          (blur)="onComposerBlur()"></div>
      </div>
      <div class="agent-panel__composer-actions">
        <button type="button" class="agent-panel__context" [matMenuTriggerFor]="contextMenu" [attr.aria-label]="'agent.inspect.title' | translate">
          <mat-icon class="agent-panel__context-icon">place</mat-icon>
          <span class="agent-panel__context-label">
            {{ contextLabelKey() | translate }}
            @if (contextEntityName()) {
              <span class="agent-panel__context-entity">· {{ contextEntityName() }}</span>
            }
          </span>
        </button>
        <mat-menu #contextMenu="matMenu">
          <div class="agent-panel__inspect" (click)="$event.stopPropagation()">
            <div class="agent-panel__inspect-title">{{ "agent.inspect.title" | translate }}</div>
            <div class="agent-panel__inspect-row">
              <span class="agent-panel__inspect-label">{{ "agent.inspect.surface" | translate }}</span>
              <span class="agent-panel__inspect-value">{{ contextLabelKey() | translate }}</span>
            </div>
            @for (detail of contextDetails(); track detail.label) {
              <div class="agent-panel__inspect-row">
                <span class="agent-panel__inspect-label">{{ detail.label | translate }}</span>
                <span class="agent-panel__inspect-value">{{ detail.value }}</span>
              </div>
            }
          </div>
        </mat-menu>
        <div class="agent-panel__composer-buttons">
          @if (voiceSupported) {
            <button
              type="button"
              mat-icon-button
              class="agent-panel__mic"
              [class.agent-panel__mic--recording]="voiceState() === 'recording'"
              [class.agent-panel__mic--busy]="voiceBusy()"
              [disabled]="voiceBusy()"
              [attr.aria-pressed]="voiceState() === 'recording'"
              [attr.aria-label]="voiceButtonLabel() | translate"
              [matTooltip]="voiceButtonLabel() | translate"
              (click)="toggleRecording()">
              <mat-icon>{{ voiceIcon() }}</mat-icon>
            </button>
          }
          @if (streaming()) {
            <button type="button" mat-icon-button class="agent-panel__send" [attr.aria-label]="'agent.cancel' | translate" (click)="cancel()">
              <mat-icon>stop</mat-icon>
            </button>
          } @else {
            <button type="submit" mat-icon-button class="agent-panel__send" [disabled]="!canSend()" [attr.aria-label]="'agent.send' | translate">
              <mat-icon>send</mat-icon>
            </button>
          }
        </div>
      </div>
      @if (voiceError()) {
        <p class="agent-panel__voice-error" role="alert">{{ voiceError() }}</p>
      }
    </form>

    <p class="agent-panel__disclaimer">{{ "agent.disclaimer" | translate }}</p>
  </aside>
}
